import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import Button from '../orders/css';
import { getPdfUrl } from '../utils/utils';
import { orderStatus, quoteStatus, wlnFilterStatuses } from '../utils/constant';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';
import { useLocaleContext } from '../context/LocaleContext';

const getLink = (id, label) => {
  const locale = useLocaleContext();
  return (
    <a
      className="download-link"
      href={getPdfUrl(id, locale)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  );
};

const Tooltip = ({ children }) => {
  const localizedStrings = useLocalizedStringsContext();
  const { tooltipMessage } = localizedStrings.srtDashboard;
  return (
    <>
      {children}
      <div
        data-type="info"
        data-tip="React-tooltip"
        style={{ display: 'inline', marginLeft: '8px' }}
      >
        <i className="fa fa-question-circle"></i>
      </div>
      <ReactTooltip type="info" effect="float">
        {tooltipMessage}
      </ReactTooltip>
    </>
  );
};

Tooltip.propTypes = {
  children: PropTypes.node.isRequired
};

const getButton = (onClick, label, isDisabled) => (
  <Button onClick={onClick} disabled={isDisabled}>
    {label}
  </Button>
);

const getStatus = (order, quote) => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: { status }
  } = localizedStrings;
  let statusOfTheOrder = null;
  if(order === orderStatus.incomplete && quote === quoteStatus.incomplete)
    statusOfTheOrder = status.incomplete;
  else if(order === orderStatus.incomplete && quote === quoteStatus.pendingApproval)
    statusOfTheOrder = status.pending;
  else if(order === orderStatus.incomplete && quote === quoteStatus.approved)
    statusOfTheOrder = status.approved;
  else if(order === orderStatus.pendingValidation && quote === quoteStatus.approved)
    statusOfTheOrder = status.pendingValidation;
  else if(order === orderStatus.validated && quote === quoteStatus.approved)
    statusOfTheOrder = status.validated;
  else if(order === orderStatus.newOrder && quote === quoteStatus.approved)
    statusOfTheOrder = status.orderIncomplete;
  else if(order === orderStatus.validated)
    statusOfTheOrder = status.validated;

  return {statusText : statusOfTheOrder, tooltip : <Tooltip>{statusOfTheOrder}</Tooltip>};
};

const compareArray = (arr1, arr2) => {
  return arr1.some(r => arr2.indexOf(r) >= 0);
};

const exactCompareArray = (arrOne, arrTwo) => {
  return (
    arrOne.length === arrTwo.length &&
    arrOne.filter((currVal, idx) => currVal !== arrTwo[idx]).length === 0
  );
};

const getOrderType = (productSelected = [], productType = '') => {
  const localizedStrings = useLocalizedStringsContext();
  const {
    srtDashboard: {
      orderType,
      wirelineProducts,
      secureBusinessProducts,
      businessFibre,
      businessCopper
    },
    contentfulValues: { standalone }
  } = localizedStrings;
  let type = '';
  if (
    compareArray(wirelineProducts, productSelected) &&
    !compareArray(secureBusinessProducts, productSelected)
  ) {
    type = orderType.wireline;
  } else if (
    !compareArray(wirelineProducts, productSelected) &&
    compareArray(secureBusinessProducts, productSelected)
  ) {
    type = orderType.secureBusiness;
  } else if (
    productType !== standalone &&
    exactCompareArray(businessFibre, productSelected) &&
    !exactCompareArray(businessCopper, productSelected)
  ) {
    type = orderType.businessBundleFibre;
  } else if (
    productType !== standalone &&
    !exactCompareArray(businessFibre, productSelected) &&
    exactCompareArray(businessCopper, productSelected)
  ) {
    type = orderType.businessBundleCopper;
  } else if (
    compareArray(wirelineProducts, productSelected) &&
    compareArray(secureBusinessProducts, productSelected)
  ) {
    type = orderType.businessBundle;
  }
  return type;
};


const filterValueToStatusesMap = {
  [wlnFilterStatuses.incomplete]: {
    quoteStatus: quoteStatus.incomplete,
    orderStatus: orderStatus.incomplete
  },
  [wlnFilterStatuses.quoteComplete]: {
    quoteStatus: quoteStatus.approved,
    orderStatus: orderStatus.incomplete
  },
  [wlnFilterStatuses.orderIncomplete]: {
    quoteStatus: quoteStatus.approved,
    orderStatus: orderStatus.newOrder
  },
  [wlnFilterStatuses.submitted]: {
    orderStatus: orderStatus.validated
  },
  [wlnFilterStatuses.pendingValidation]: {
    orderStatus: orderStatus.pendingValidation
  },
  [wlnFilterStatuses.inProgress]: {
    orderStatus: orderStatus.inProgress
  },
  [wlnFilterStatuses.actionRequired]: {
    orderStatus: orderStatus.actionRequired
  },
  [wlnFilterStatuses.completed]: {
    orderStatus: orderStatus.completed
  },
  [wlnFilterStatuses.orderCanceled]: {
    orderStatus: orderStatus.canceled
  },
  [wlnFilterStatuses.waitingOnCustomerResponse]: {
    orderStatus: orderStatus.waitingOnCustomerResponse
  },
  [wlnFilterStatuses.waitingOnCredit]: {
    orderStatus: orderStatus.waitingOnCredit
  },
  [wlnFilterStatuses.waitingOnInternet]: {
    orderStatus: orderStatus.waitingOnInternet
  },
  [wlnFilterStatuses.waitingOnNTAOrDSR]: {
    orderStatus: orderStatus.waitingOnNTAOrDSR
  },
  [wlnFilterStatuses.responded]: {
    orderStatus: orderStatus.responded
  }
};

const getStatusFiltersPayload = filters => {
  const quoteStatuses = [];
  const statuses = [];
  filters.forEach(element => {
    if (filterValueToStatusesMap[element.value]) {
      const correspondingOrderStatus =
        filterValueToStatusesMap[element.value].orderStatus;
      const correspondingQuoteStatus =
        filterValueToStatusesMap[element.value].quoteStatus;
      if (
        correspondingOrderStatus &&
        !statuses.includes(correspondingOrderStatus)
      )
        statuses.push(correspondingOrderStatus);
      if (
        correspondingQuoteStatus &&
        !quoteStatuses.includes(correspondingQuoteStatus)
      )
        quoteStatuses.push(correspondingQuoteStatus);
    }
  });
  return { status: statuses, quoteStatus: quoteStatuses };
};

export { getLink, getButton, getStatus, getOrderType, getStatusFiltersPayload };
